// What every Custom API push did, call by call.
//
// A push is once-only per (provider, call), held while the calling number is
// unknown, retried only when the far end gave no answer, and final once it did.
// All of that is recorded on custom_api_deliveries and none of it was visible
// without a SQL query — so a Yamurai report that never got its reference number
// looked exactly like one that did.
//
// Read-only, and deliberately thin: the endpoint returns no request or response
// body (they carry the caller's details) and the caller's number arrives masked
// to its last four digits. Nothing here can unmask it.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, AlertCircle, CheckCircle2, Clock, XCircle, Hourglass, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  customApiService, crmProvidersService, CustomApiDelivery, CustomApiDeliveryStatus, CrmProvider,
} from 'api/services/crmProviders.service';
import { aiAgentsService } from 'api/services/aiAgents.service';
import { useAuth } from 'hooks/useAuth';
import { formatDate } from 'utils/helpers';
import { DELIVERY_STATUS, DELIVERY_STATUS_ORDER, matchesDelivery } from './customApiShapes';

const TONE: Record<string, { cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  ok: { cls: 'text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle2 },
  wait: { cls: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300', icon: Hourglass },
  retry: { cls: 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300', icon: Clock },
  bad: { cls: 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
};

const SIZE = 50;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

const CustomApiDeliveries: React.FC = () => {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id && currentOrganization.id !== 'synthetic-org' ? currentOrganization.id : '';

  // Server-side filters: what the endpoint takes (organizationId, status, from, to, page, size).
  const [scope, setScope] = useState<'current' | 'all'>('current');
  const [status, setStatus] = useState<CustomApiDeliveryStatus | ''>('');
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [page, setPage] = useState(0);
  // Client-side narrowing of the loaded page — the endpoint has no such parameters.
  const [providerId, setProviderId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [callId, setCallId] = useState('');

  const [rows, setRows] = useState<CustomApiDelivery[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [providers, setProviders] = useState<CrmProvider[]>([]);
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const res = await customApiService.deliveries({
      organizationId: scope === 'current' ? orgId || undefined : undefined,
      status, from, to, page, size: SIZE,
    });
    if (res.success && res.data) {
      setRows(res.data.deliveries);
      setTotal(res.data.total);
      setError(null);
    } else {
      setError(res.error || 'Could not load deliveries');
    }
    setLoading(false);
  }, [scope, orgId, status, from, to, page]);

  useEffect(() => { load(); }, [load]);

  // Names for the ids the rows carry — best effort, this organisation only. A row
  // from another organisation (the "all" scope) shows its id instead.
  useEffect(() => {
    (async () => {
      const [p, a] = await Promise.all([crmProvidersService.list(), aiAgentsService.list()]);
      if (p.success) setProviders((p.data || []).filter(x => x.providerType === 'CUSTOM_API'));
      if (a.success) {
        const m: Record<string, string> = {};
        (a.data || []).forEach(x => { if (x.id != null) m[String(x.id)] = x.name; });
        setAgentNames(m);
      }
    })();
  }, [orgId]);

  const providerName = (id: string) => providers.find(p => p.id === id)?.name || `${id.slice(0, 8)}…`;
  const shown = useMemo(
    () => rows.filter(d => matchesDelivery(d, { providerId, agentId, callId })),
    [rows, providerId, agentId, callId],
  );
  const pages = Math.max(1, Math.ceil(total / SIZE));
  const narrowed = !!(providerId || agentId || callId);

  const input = 'px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm';
  const lbl = 'block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-0.5';
  // Any change of a server filter starts again at the first page.
  const refilter = <T,>(fn: (v: T) => void) => (v: T) => { setPage(0); fn(v); };

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <Link to="/crm/integrations" className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Booking &amp; CRM
      </Link>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Custom API deliveries</h1>
          <p className="text-sm mt-1 max-w-3xl" style={{ color: 'var(--text-muted)' }}>
            Every push to a Custom API — one row per provider and call, however many times it was
            tried. Bodies are never shown and callers are masked to the last four digits.
          </p>
        </div>
        <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      <div className="tl-card flex flex-wrap items-end gap-3">
        <div>
          <label className={lbl}>Organisation</label>
          <select className={input} value={scope} onChange={e => refilter(setScope)(e.target.value as 'current' | 'all')}>
            <option value="current">{currentOrganization?.name || 'Current organisation'}</option>
            <option value="all">Every organisation I can reach</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Status</label>
          <select className={input} value={status} onChange={e => refilter(setStatus)(e.target.value as CustomApiDeliveryStatus | '')}>
            <option value="">Any</option>
            {DELIVERY_STATUS_ORDER.map(s => <option key={s} value={s}>{DELIVERY_STATUS[s].label}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>From (UTC day)</label>
          <input type="date" className={input} value={from} max={to} onChange={e => refilter(setFrom)(e.target.value)} />
        </div>
        <div>
          <label className={lbl}>To (UTC day)</label>
          <input type="date" className={input} value={to} min={from} onChange={e => refilter(setTo)(e.target.value)} />
        </div>
        <div className="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1" />
        <div>
          <label className={lbl}>Provider</label>
          <select className={input} value={providerId} onChange={e => setProviderId(e.target.value)}>
            <option value="">Any</option>
            {providers.map(p => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Agent id</label>
          <input className={input + ' w-24'} value={agentId} inputMode="numeric"
                 onChange={e => setAgentId(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>
        <div>
          <label className={lbl}>Call id</label>
          <input className={input + ' w-44 font-mono text-xs'} value={callId} onChange={e => setCallId(e.target.value)} />
        </div>
      </div>
      {narrowed && (
        <p className="text-[11px] -mt-2" style={{ color: 'var(--text-muted)' }}>
          Provider, agent and call narrow this page only ({rows.length} loaded) — the server filters by
          organisation, status and day.
        </p>
      )}

      {error && (
        <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm
                        text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}

      {!loading && !error && shown.length === 0 && (
        <div className="tl-card text-center py-10">
          <p className="text-sm text-gray-500">No deliveries {narrowed ? 'match' : 'in this range'}.</p>
          <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
            A row appears when an agent bound to a Custom API captures a lead — on the call, or
            recovered after it.
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <div className="tl-card !p-0 overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide border-b border-gray-200 dark:border-gray-700"
                  style={{ color: 'var(--text-muted)' }}>
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Agent</th>
                <th className="px-3 py-2 font-medium">Call</th>
                <th className="px-3 py-2 font-medium">Caller</th>
                <th className="px-3 py-2 font-medium text-right">Attempts</th>
                <th className="px-3 py-2 font-medium">Reference</th>
                <th className="px-3 py-2 font-medium">Last error</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(d => {
                const st = DELIVERY_STATUS[d.status] || { label: d.status, tone: 'wait', means: '' };
                const tone = TONE[st.tone];
                const Icon = tone.icon;
                const isOpen = open === d.id;
                return (
                  <React.Fragment key={d.id}>
                    <tr className="border-b border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer align-top"
                        onClick={() => setOpen(isOpen ? null : d.id)}>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(d.createdAt || '') || '—'}</td>
                      <td className="px-3 py-2">
                        <span title={st.means} className={`text-[10.5px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 whitespace-nowrap ${tone.cls}`}>
                          <Icon className="w-3 h-3" />{st.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">{providerName(d.providerId)}</td>
                      <td className="px-3 py-2">{d.agentId == null ? '—' : (agentNames[String(d.agentId)] || `#${d.agentId}`)}</td>
                      <td className="px-3 py-2 font-mono text-[11px] break-all">{d.callId || '—'}</td>
                      <td className="px-3 py-2 font-mono">{d.caller || '—'}</td>
                      <td className="px-3 py-2 text-right">{d.attempts}</td>
                      <td className="px-3 py-2 font-mono">{d.reference || '—'}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate text-red-700 dark:text-red-300" title={d.lastError || ''}>
                        {d.lastError || ''}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/40">
                        <td colSpan={9} className="px-3 py-2.5 text-[12px] space-y-1">
                          <div style={{ color: 'var(--text-muted)' }}>{st.means}</div>
                          <div>
                            <span className="font-medium">Origin</span> {d.origin === 'IN_CALL' ? 'on the call' : 'recovered after the call'}
                            {' · '}<span className="font-medium">HTTP</span> {d.httpStatus ?? '—'}
                            {' · '}<span className="font-medium">Success field</span> {d.success == null ? '—' : String(d.success)}
                            {' · '}<span className="font-medium">Sent</span> {d.sentAt ? formatDate(d.sentAt) : '—'}
                          </div>
                          {d.errorMessages && (
                            <div><span className="font-medium">Their errors</span> <span className="text-red-700 dark:text-red-300">{d.errorMessages}</span></div>
                          )}
                          {d.lastError && (
                            <div className="break-all"><span className="font-medium">Last error</span> {d.lastError}</div>
                          )}
                          {d.agentValues && Object.keys(d.agentValues).length > 0 && (
                            <div>
                              <span className="font-medium">Agent values</span>{' '}
                              {Object.entries(d.agentValues).map(([k, v]) => (
                                <code key={k} className="mr-2">{k}={typeof v === 'object' ? JSON.stringify(v) : String(v)}</code>
                              ))}
                            </div>
                          )}
                          <div style={{ color: 'var(--text-muted)' }}>
                            delivery {d.id} · provider {d.providerId} · organisation {d.organizationId}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > SIZE && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span style={{ color: 'var(--text-muted)' }}>Page {page + 1} of {pages} · {total} in all</span>
          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={page + 1 >= pages || loading} onClick={() => setPage(page + 1)}>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {total > 0 && total <= SIZE && (
        <p className="text-[11px] text-right" style={{ color: 'var(--text-muted)' }}>{total} in all</p>
      )}
    </div>
  );
};

export default CustomApiDeliveries;
